import { NgOptimizedImage } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  inject,
  NgModule,
  NO_ERRORS_SCHEMA,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule, RouteReuseStrategy } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import * as Sentry from '@sentry/angular';
import { AgGridModule } from 'ag-grid-angular';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { ToastrModule } from 'ngx-toastr';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routes';
import { MainLayoutComponent } from './layouts/main-layout.component';
import { NotificationDropdown } from './layouts/user-notifications/notification-dropdown/notification-dropdown.component';
import { NotificationModalComponent } from './shared/components/notification-modal/notification-modal.component';
import { PopupMediaPlayerComponent } from './shared/components/popup-media-player/popup-media-player.component';
import { PushNotificationComponent } from './shared/components/push-notification/push-notification.component';
import { AuthGuard } from './shared/guards/auth.guard';
import { MtmAdminGuard } from './shared/guards/mtm-admin.guard';
import { NoAuthGuard } from './shared/guards/no-auth.guard';
import { AppHttpInterceptor } from './shared/interceptors/http.interceptor';
import { SimpleChatService } from './shared/services/simple-chat.service';
import { WEBAPP_CONTEXT_KEYS } from './shared/services/view-context-keys';
import { LogService } from './shared/services/log.service';
import { TeamService } from './shared/services/team.service';
import { SharedModule } from './sub-modules/SharedModule';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { ColorPickerDirective } from 'ngx-color-picker';
import { MarkdownModule, SANITIZE } from 'ngx-markdown';

import { QuillModule } from 'ngx-quill';
import { environment } from '../environments/environment';
import { AdminService } from './admin/services/admin.service';
import { ActivityLogButton } from './layouts/activity-log/activity-log-button/activity-log-button.component';
import { ChildLayoutComponent } from './layouts/child-layout/child-layout.component';

import { TopNavComponent } from './layouts/top-nav/top-nav.component';
import { TopNavDriveCompareBtnComponent } from './layouts/top-nav/top-nav-drive-compare-btn/top-nav-drive-compare-btn.component';
import { TopNavDriveShareBtnComponent } from './layouts/top-nav/top-nav-drive-share-btn/top-nav-drive-share-btn.component';
import { DriveCommentsPanelComponent } from './shared/drive/drive-comments-panel/drive-comments-panel.component';
import { DriveRightRailComponent } from './shared/drive/drive-right-rail/drive-right-rail.component';
import { TopNavVotingComponent } from './layouts/top-nav/top-nav-voting/top-nav-voting.component';
import { TopNavCompareBtnComponent } from './layouts/top-nav/top-nav-compare-btn/top-nav-compare-btn.component';
import { TopNavFullscreenBtnComponent } from './layouts/top-nav/top-nav-fullscreen-btn/top-nav-fullscreen-btn.component';
import { AssetExportModalComponent } from './shared/components/asset-export-modal/asset-export-modal.component';
import { FileModalComponent } from './shared/components/hierarchical-files/file-modal/file-modal.component';
import { FilePreviewModalComponent } from './shared/components/hierarchical-files/file-preview-modal/file-preview-modal.component';
import { DeleteModelConfirmationModalComponent } from './shared/components/manage-asset-list-modal/delete-model-confirmation-modal/delete-model-confirmation-modal.component';
import { ManageAssetListActionsComponent } from './shared/components/manage-asset-list-modal/manage-asset-list-actions/manage-asset-list-actions.component';
import { ManageAssetListModalComponent } from './shared/components/manage-asset-list-modal/manage-asset-list-modal.component';
import { SaveModelConfirmationModalComponent } from './shared/components/manage-asset-list-modal/save-model-confirmation-modal/save-model-confirmation-modal.component';
import { MTMFileDownloadService } from './shared/components/mtm-file-download/mtm-file-download.service';
import { MTMToasterComponent } from './shared/components/mtm-toaster/mtm-toaster.component';
import { WarningInactiveModalComponent } from './shared/components/warning-inactive-modal/warning-inactive-modal.component';
import { WarningLoggedoutModalComponent } from './shared/components/warning-loggedout-modal/warning-loggedout-modal.component';
import { ProjectCreationGuard } from './shared/guards/project-creation.guard';
import { HttpCancelInterceptor } from './shared/interceptors/cancel-interceptor';
import { composerSanitizeHtml } from './shared/base-functions/sanitize';
import { archivedProjectFilterPipe } from './shared/pipes/archived-project-filter.pipe';
import { MtmDatePipe } from './shared/pipes/mtm-date.pipe';
import { MtmFileSizePipe } from './shared/pipes/mtm-file-size.pipe';
import { MtmTimePipe } from './shared/pipes/mtm-time.pipe';
import { projectFilterPipe } from './shared/pipes/project-filter.pipe';
import { TranslatePipe } from './shared/pipes/translate.pipe';
import { ApiService } from './shared/services/api.service';
import { AuthService } from './shared/services/auth.service';
import { BroadcastService } from './shared/services/broadcast.service';
import { ClickableNotificationService } from './shared/services/clickable-notification.service';
import { CommonService } from './shared/services/common.service';
import { CompanyService } from './shared/services/company.service';
import { ComponentStateService } from './shared/services/component-state.service';
import { CurrencyService } from './shared/services/currency.service';
import { DateHelperService } from './shared/services/date-helper.service';
import { EmitterService } from './shared/services/emitter.service';
import { UserflowService } from './shared/services/userflow.service';
import { EventService } from './shared/services/event.service';
import { HelperService } from './shared/services/helper.service';
import { HttpCancelService } from './shared/services/http-cancel.service';
import { NotificationService } from './shared/services/notification.service';
import { OverlayService } from './shared/services/overlayService';
import { PermissionService } from './shared/services/permissions.service';
import { PlatformEventService } from './shared/services/platform-events.service';
import { ProjectService } from './shared/services/project.service';
import { ProjectSectionService } from './shared/services/project-section.service';
import { ProjectUserService } from './shared/services/project-user.service';
import { ProjectFileService } from './shared/services/project-file.service';
import { ProposalInvitationService } from './shared/services/proposal-invitation.service';
import { RegistrationService } from './shared/services/registration.service';
import { SignedUrlService } from './shared/services/signedurl.service';
import { SubsectionService } from './shared/services/subsection.service';
import { UnseenNotifierService } from './shared/services/unseen-notifier.service';
import { UploadService } from './shared/services/upload.service';
import { UserService } from './shared/services/user.service';
import { ValidatorService } from './shared/services/validator.service';
import { WorkspaceService } from './shared/services/workspace.service';
import { ProjectSharedModule } from './sub-modules/project-modules/project-SharedModule';
import { ZoomInOutComponent } from './workspace-v2/analytics/zoom-in-out/zoom-in-out.component';
import { CampaignWorkspaceModule } from './workspace-v2/campaign-workspace.module';
import { WorkspaceModule } from './workspace/workspace.module';
import { MoveToModalComponent } from './shared/components/move-to-modal/move-to-modal.component';
import { AssetModelComponent } from './shared/components/asset-model/asset-model.component';
import { LeftSidebarFloatingComponent } from './layouts/left-sidebar-floating/left-sidebar-floating.component';
import { IdleExpiry, NgIdleModule } from '@ng-idle/core';
import { SharedIdleExpiry } from './shared/services/shared-idle-expiry';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { PlatformCommentsChatModule } from './shared/components/platform-comments-chat/platform-comments-chat.module';
import { MtmSashVerticalComponent } from './shared/components/mtm-sash-vertical/mtm-sash-vertical.component';
import { NotificationConfirmationAdapter } from './shared/services/notification-confirmation-adapter.service';
import { ConfirmationService } from '@mtm/conversation-ui-angular';
import { NoReuseOnParamChangeStrategy } from './shared/routing/no-reuse-on-param-change.strategy';
import { OldMediaPlayerModule } from './shared/components/media-player/old-media-player.module';

import { DownloadAssetButtonComponent } from './shared/components/download-asset-button/download-asset-button.component';
import { AssetInfoToggleComponent } from './shared/components/asset-info-toggle/asset-info-toggle.component';
import { VersionDropdownComponent } from './shared/components/version-dropdown/version-dropdown.component';
import {
  provideTaskUI,
  ConfirmationService as TaskUiConfirmationService,
  TaskUnavailableHandler,
  TASK_CONTEXT_KEYS,
  type EnvironmentName,
} from '@mtm/task-ui-angular';
import { provideMtmContext } from '@mtm/context-ui-angular';
import { DRIVE_CONTEXT_KEYS, provideDriveContextMinter } from '@mtm/drive/components';
import { TaskUiConfirmationAdapter } from './shared/services/task-ui-confirmation-adapter.service';
import { TaskUiUnavailableAdapter } from './shared/services/task-ui-unavailable-adapter.service';
import { TaskUserSettingsProviderService } from './shared/services/task-ui-user-settings-provider.service';
import { PlatformTaskInterfaceModule } from './shared/components/platform-task-interface/platform-task-interface.module';
import { provideMtmAuth } from './shared/auth/mtm-auth.providers';
import { mtmAppTokenInterceptor } from '@mtm/auth-ui-angular';
import { provideMtmDrive } from './shared/drive/drive.providers';
import { provideMtmMediaStudio } from './shared/drive/media-studio.providers';
import { currentAppToken } from './shared/auth/app-token';
ModuleRegistry.registerModules([AllCommunityModule]);

@NgModule({
  declarations: [
    AppComponent,
    MainLayoutComponent,
    ChildLayoutComponent,
    TopNavComponent,
    TopNavVotingComponent,
    TopNavCompareBtnComponent,
    TopNavFullscreenBtnComponent,
    NotificationModalComponent,
    PopupMediaPlayerComponent,
    NotificationDropdown,
    LeftSidebarFloatingComponent,
    PushNotificationComponent,
    ActivityLogButton,
    FileModalComponent,
    FilePreviewModalComponent,
    MTMToasterComponent,
    WarningInactiveModalComponent,
    WarningLoggedoutModalComponent,
    ZoomInOutComponent,
    AssetExportModalComponent,
    ManageAssetListModalComponent,
    ManageAssetListActionsComponent,
    SaveModelConfirmationModalComponent,
    DeleteModelConfirmationModalComponent,
    MoveToModalComponent,
    AssetModelComponent,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    MarkdownModule.forRoot({
      sanitize: {
        provide: SANITIZE,
        useValue: composerSanitizeHtml,
      },
    }),
    ToastrModule.forRoot(),
    NgIdleModule.forRoot(),
    DragDropModule,
    NgClickOutsideDirective,
    NgxSliderModule,
    ProjectSharedModule,
    SharedModule,
    AppRoutingModule,
    WorkspaceModule,
    ColorPickerDirective,
    AgGridModule,
    QuillModule.forRoot(),
    CampaignWorkspaceModule,
    NgOptimizedImage,
    PlatformCommentsChatModule,
    MtmSashVerticalComponent,
    OldMediaPlayerModule,
    DownloadAssetButtonComponent,
    AssetInfoToggleComponent,
    VersionDropdownComponent,
    PlatformTaskInterfaceModule,
    TopNavDriveCompareBtnComponent,
    TopNavDriveShareBtnComponent,
    DriveCommentsPanelComponent,
    DriveRightRailComponent,
  ],
  providers: [
    SharedIdleExpiry,
    { provide: IdleExpiry, useExisting: SharedIdleExpiry },
    UserflowService,
    EmitterService,
    ApiService,
    AuthService,
    ProjectService,
    ProjectSectionService,
    ProjectUserService,
    ProjectFileService,
    PermissionService,
    UserService,
    CommonService,
    OverlayService,
    SignedUrlService,
    AdminService,
    UploadService,
    CompanyService,
    NotificationService,
    UnseenNotifierService,
    HelperService,
    DateHelperService,
    SimpleChatService,
    RegistrationService,
    ProposalInvitationService,
    BroadcastService,
    TeamService,
    ValidatorService,
    EventService,
    NoAuthGuard,
    AuthGuard,
    MtmAdminGuard,
    ProjectCreationGuard,
    TranslatePipe,
    MTMFileDownloadService,
    SubsectionService,
    WorkspaceService,
    HttpCancelService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppHttpInterceptor,
      multi: true,
    },
    MtmDatePipe,
    CurrencyService,
    ClickableNotificationService,
    MtmTimePipe,
    projectFilterPipe,
    MtmFileSizePipe,
    archivedProjectFilterPipe,
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCancelInterceptor,
    },
    ComponentStateService,
    PlatformEventService,
    ...(environment.sentry.dsn
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
              showDialog: false,
            }),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
          },
        ]
      : []),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([mtmAppTokenInterceptor(environment.drive.apiUrl)]),
    ),
    // The shared view context: pulls `?ctx=<uuid>` into memory at startup
    // (its app initializer; bounded so a slow fetch can't hold up bootstrap)
    // and re-hydrates on every navigation. Costs nothing when there is no
    // `ctx`, and a failed fetch falls back to the defaults. The task panel's
    // and drive explorer's keys are registered here too, so they are known
    // before the first hydration — @mtm/task-ui-angular and @mtm/drive read
    // and write them themselves.
    provideMtmContext({
      apiUrl: environment.context.apiUrl,
      keys: [...WEBAPP_CONTEXT_KEYS, ...TASK_CONTEXT_KEYS, ...DRIVE_CONTEXT_KEYS],
      // `mtmAppTokenInterceptor` above is scoped to `environment.drive.apiUrl`,
      // and the context service is a DIFFERENT origin — so its requests would
      // otherwise carry no Authorization header at all and lean entirely on
      // the cookie. Supply the token directly, as the drive playground does.
      authTokenProvider: () => currentAppToken(),
      // Failures go to LogService/Sentry, as the replaced in-house services
      // did; the SDK's debug/info chatter stays out of the console.
      logger: () => {
        const logService = inject(LogService);
        return {
          debug: () => undefined,
          info: () => undefined,
          warn: (message: string, ...args: unknown[]) =>
            logService.logMessage('warning', message, { args }),
          error: (message: string, ...args: unknown[]) =>
            logService.logMessage('error', message, { args }),
        };
      },
    }),
    // Detached ?ctx= minting — enables the drive tree's "Open in a new window".
    // Same context service and token source as `provideMtmContext` above.
    ...provideDriveContextMinter({
      apiUrl: environment.context.apiUrl,
      authTokenProvider: () => currentAppToken(),
    }),
    ...provideMtmAuth(),
    ...provideMtmDrive(),
    // The feed's body-appended file preview pop-in resolves from this root injector.
    ...provideMtmMediaStudio(),
    ...provideTaskUI({
      env: environment.environmentType as EnvironmentName,
      userSettings: () => inject(TaskUserSettingsProviderService).settings,
    }),
    {
      provide: ConfirmationService,
      useClass: NotificationConfirmationAdapter,
    },
    {
      provide: TaskUiConfirmationService,
      useClass: TaskUiConfirmationAdapter,
    },
    {
      provide: TaskUnavailableHandler,
      useClass: TaskUiUnavailableAdapter,
    },
    {
      provide: RouteReuseStrategy,
      useClass: NoReuseOnParamChangeStrategy,
    },
  ],
})
export class AppModule {}
